import { getJSONByUrl } from 'widgets/toolbox/ajax';

import { TPaymentAccordionItemInstance } from 'widgets/checkout/PaymentAccordionItem';

type TOrder = {
    billing: {
        payment: {
            applicablePaymentMethods: [
                {
                    [key: string]: string;
                }
            ];
        };
    };
};
interface IPaymentMethodsResponse {
    [renderedOptions: string]: Record<string, string>;
}

const isPaymentMethodsResponse = (value: Record<string, unknown>): value is IPaymentMethodsResponse => {
    return Object.hasOwnProperty.call(value, 'renderedOptions');
};

/**
 * @param Accordion Base widget for extending
 * @returns Payment Accordion class
 */
function PaymentAccordionClassCreator(Accordion: import('widgets/global/Accordion').TAccordion) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class PaymentAccordion
     * @augments Accordion
     * @classdesc Represents PaymentAccordion components that implements logic to validate\submit payment information
     * It has next features:
     * 1. Validates current payment accordion item
     * 2. Handles current payment tab submit by processing current tab data
     *
     * @example <caption>Example of PaymentAccordion widget usage</caption>
     *    <div
     *        data-id="payment"
     *        data-widget="paymentAccordion"
     *        data-forward-to-parent="hidePaymentError:submitCurrentStep:showPaymentError:toggleNextPaymentButton:toggleBillingForm"
     *        data-allow-toggle="false"
     *        data-allow-multiple="false"
     *        data-error-message="${Resource.msg('error.payment.not.selected','checkout',null)}"
     *        data-single-panel="${pdict.order.billing.payment.applicablePaymentMethods.length === 1}"
     *        data-field-name="${pdict.forms.billingForm.paymentMethod.htmlName}"
     *        data-payment-options-url="${URLUtils.url('CheckoutServices-GetPaymentOptions')}"
     *        ${pdict.hidePaymentOptions ? 'hidden' : ''}
     *        data-disabled="${Boolean(pdict.hidePaymentOptions)}"
     *        class="b-payment_accordion ${pdict.order.billing.payment.isZeroTotal ? 'm-zero_payment' : ''}"
     *    >
     *        <div data-ref="container">
     *            <isloop items="${pdict.order.billing.payment.applicablePaymentMethods}" var="paymentOption">
     *                <isinclude template="${require('util/pref').get('checkout.payment.body.template.' + paymentOption.ID, 'checkout/billing/paymentOptions/paymentBody_Default')}"/>
     *            </isloop>
     *        </div>
     *    </div>
     *
     * @property {string} data-field-name - payment field name
     */
    class PaymentAccordion extends Accordion {
        applicablePaymentMethods: TOrder['billing']['payment']['applicablePaymentMethods']|undefined;

        disabledPaymentMethods: [string] | [] | undefined;

        /**
         * @description Returns Widget configuration object
         * @returns result
         */
        prefs() {
            return {
                fieldName: '',
                singlePanel: false,
                errorMessageLabel: 'errorMessageLabel',
                errorMessage: 'Please select payment method',
                paymentOptionsUrl: '',
                disabled: false,
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         * @listens "order.updated"
         */
        init() {
            super.init();

            this.eventBus().on('order.updated', 'updatePaymentOptions');
        }

        /**
         * @description Validates current payment accordion item children
         * @returns result
         */
        validate(): boolean {
            const currentPaymentItem = this.getCurrentPaymentItem();

            if (currentPaymentItem) {
                const isValid = currentPaymentItem.isChildrenValid();

                if (!isValid && !currentPaymentItem.isPanelOpen) {
                    currentPaymentItem.openPanel();
                }

                return isValid;
            }

            return false;
        }

        /**
         * @description Handles current payment tab submit
         * @param attributes need attributes for payment items
         */
        process(attributes: Record<string, unknown>): Promise<Record<string, unknown>> {
            if (this.prefs().disabled) {
                return Promise.resolve({});
            }

            return new Promise((resolve, reject) => {
                const valid = this.validate();
                const currentPaymentItem = this.getCurrentPaymentItem();

                if (!currentPaymentItem) {
                    reject(new Error(this.prefs().errorMessage));

                    return;
                }

                if (!valid) {
                    reject();

                    return;
                }

                if (currentPaymentItem) {
                    const tabProcessResult = currentPaymentItem.process(attributes).then(data => {
                        // @ts-expect-error data should be object in order to apply spread operator
                        return { [this.prefs().fieldName]: currentPaymentItem.prefs().methodId, ...data };
                    });

                    resolve(tabProcessResult);

                    return;
                }

                reject();
            });
        }

        /**
         * @description Handles current payment tab error
         * @param fieldName - field name with error
         * @param errorMsg - error message
         */
        handleError(fieldName: string, errorMsg: string) {
            const currentPaymentItem = this.getCurrentPaymentItem();

            if (currentPaymentItem) {
                currentPaymentItem.handleError({
                    fieldName,
                    errorMsg
                });
            }
        }

        /**
         * @description Returns current payment accordion item
         * @returns result
         */
        getCurrentPaymentItem() {
            return this.getCurrentOpenItem<TPaymentAccordionItemInstance>();
        }

        /**
         * @description Clean up sensitive data handler
         * @param stage Stage on which data is going to be cleared
         */
        cleanupSensitiveData(stage: string) {
            this.closeItems();

            this.eachChild((item: TPaymentAccordionItemInstance) => {
                item.data('cleanupSensitiveData', stage);
            });
        }

        /**
         * @description Handle activation logic for payment accordion
         * Uses on payment integration
         */
        activate() {
            if (this.prefs().singlePanel) {
                this.openFirstItem();
            }
        }

        /**
         * @description Get ids list of rendered payment methods
         */
        getRenderedMethods(): Array<string> {
            const methods: Array<string> = [];

            this.eachChild<TPaymentAccordionItemInstance>(item => {
                const methodId = item.data<string>('methodId');

                if (methodId) {
                    methods.push(methodId);
                }
            });

            return methods;
        }

        /**
         * @description Get payment accordion item based on method id
         * @param methodId - Payment method id
         */
        getMethod(methodId: string) {
            let method: null|TPaymentAccordionItemInstance = null;

            this.eachChild<TPaymentAccordionItemInstance>(item => {
                if (methodId === item.data<string>('methodId')) {
                    method = item;
                }
            });

            return method;
        }

        /**
         * @description Adjust payment methods list after order updating
         * @param order - Order model
         */
        updatePaymentOptions(order: TOrder) {
            if (this.prefs().disabled || !this.isUpdatingRequired(order)) {
                return;
            }

            const renderedPaymentMethods = this.getRenderedMethods();
            const applicablePaymentMethods = this.getApplicablePayments(order);
            const methodsToRender: Array<string> = [];
            const methodsToDelete: Array<string> = [];
            const applicablePaymentMethodIds: Array<string> = [];

            applicablePaymentMethods.forEach(applicablePaymentMethod => {
                if (!renderedPaymentMethods.includes(applicablePaymentMethod.ID)) {
                    methodsToRender.push(applicablePaymentMethod.ID);
                }

                applicablePaymentMethodIds.push(applicablePaymentMethod.ID);
            });

            renderedPaymentMethods.forEach(renderedPaymentMethod => {
                if (!applicablePaymentMethodIds.includes(renderedPaymentMethod)) {
                    methodsToDelete.push(renderedPaymentMethod);
                }
            });

            if (methodsToDelete.length) {
                this.removePaymentOption(methodsToDelete);
            }

            if (methodsToRender.length) {
                this.addPaymentMethods(methodsToRender, applicablePaymentMethodIds);
            }

            this.applicablePaymentMethods = applicablePaymentMethods;
        }

        isUpdatingRequired(order) {
            if (!order || !this.applicablePaymentMethods) {
                return true;
            }

            const currMethods = this.getApplicablePayments(order).map(method => method.ID);
            const prevMethods = this.applicablePaymentMethods.map(method => method.ID);

            return currMethods.filter(method => !prevMethods.includes(method))
                .concat(prevMethods.filter(method => !currMethods.includes(method)))
                .length > 0;
        }

        getApplicablePayments(order) {
            return order.billing.payment.applicablePaymentMethods;
        }

        /**
         * @description Get payment methods content and render them.
         * @param methodsToAdd - Methods ids that should be added
         * @param applicablePaymentMethodIds - Applicable payment methods ids that helps to find append position
         */
        addPaymentMethods(methodsToAdd: Array<string> = [], applicablePaymentMethodIds: Array<string>) {
            return getJSONByUrl(this.prefs().paymentOptionsUrl, {
                methods: methodsToAdd.join(',')
            }).then((response) => {
                if (isPaymentMethodsResponse(response)) {
                    Object.entries(response.renderedOptions).forEach(([method, methodContent]) => {
                        const index = applicablePaymentMethodIds.indexOf(method);

                        this.ref('container').append(methodContent, index);
                    });
                }
            });
        }

        /**
         * @description Remove payment methods that are not applicable after order updating.
         * @param methods - Methods ids that should be removed
         */
        removePaymentOption(methods: Array<string>) {
            this.eachChild<TPaymentAccordionItemInstance>(item => {
                const methodId = String(item.data('methodId'));

                if (methods.includes(methodId)) {
                    if (item.isPanelOpen) {
                        this.closeItems();
                    }

                    item.ref('self').remove();
                }
            });
        }

        /**
         * This is an adyen specific method, declaring it here to be able to override in CorePaymentAccordion
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        handleChildInit(child: TPaymentAccordionItemInstance) {}
    }

    return PaymentAccordion;
}

export type TPaymentAccordion = ReturnType<typeof PaymentAccordionClassCreator>;

export type TPaymentAccordionInstance = InstanceType<TPaymentAccordion>;

export default PaymentAccordionClassCreator;
